import { useState } from 'preact/hooks';
import { Link } from '../Link.tsx';
import { docsPages } from '../../pages/docs/docsNav.ts';
import { useT } from '../../i18n.ts';

interface MarketingMobileMenuProps {
  // Collapse the menu after an in-menu tap. Route changes also close it
  // (see MarketingLayout), but the Log in / Sign up links leave the SPA for
  // the product app, so closing on tap keeps the panel from lingering over
  // the page during the cross-origin unload.
  onNavigate: () => void;
}

// Stacked version of the header nav, dropped below the bar on phone-width
// viewports where the inline `.nav-links` would crowd. The desktop Learn More
// dropdown collapses to an in-place disclosure here so the whole menu stays in
// one column.
export function MarketingMobileMenu({ onNavigate }: MarketingMobileMenuProps) {
  const t = useT();
  const [docsOpen, setDocsOpen] = useState(false);

  return (
    <div id="mkt-mobile-menu" className="mkt-mobile-menu">
      <div className="mkt-mobile-nav" data-gp-name="mobile-nav">
        <div className={`mkt-mobile-group ${docsOpen ? 'is-open' : ''}`}>
          <button
            type="button"
            className="mkt-mobile-link mkt-mobile-disclosure"
            aria-expanded={docsOpen}
            onClick={() => setDocsOpen((o) => !o)}
          >
            {t('learn-more-menu.trigger')}
            <svg
              className="mkt-mobile-chevron"
              viewBox="0 0 10 6"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {docsOpen && (
            <div className="mkt-mobile-sublist">
              {docsPages.map((page) => (
                <Link
                  key={page.path}
                  to={page.path}
                  className="mkt-mobile-sublink"
                  onClick={onNavigate}
                >
                  {t(page.labelKey)}
                </Link>
              ))}
            </div>
          )}
        </div>
        <Link to="/demo" className="mkt-mobile-link" onClick={onNavigate}>
          Demo
        </Link>
        <Link to="/pricing" className="mkt-mobile-link" onClick={onNavigate}>
          Pricing
        </Link>
        {/* Cross-origin into the product app, mirroring the desktop nav: the
            log-in link targets the protected /dashboard route (which bounces
            anonymous visitors through /login) and uses the shorter "Sign up"
            CTA key. */}
        <Link to="/dashboard" className="mkt-mobile-link" onClick={onNavigate}>
          {t('marketing.cta.login')}
        </Link>
        <Link
          to="/register"
          className="mkt-mobile-link mkt-mobile-cta"
          onClick={onNavigate}
        >
          {t('marketing.cta.signup')}
        </Link>
      </div>
    </div>
  );
}
